/**
 * Live telemetry — counters and RSSI/SNR series derived from the line flow for the graph
 * view. Built on the **display-only** [decode](./decode.ts); it never touches the log,
 * which stays byte-verbatim (REQ-CON-9). A misread here costs a wrong pixel, not a wrong
 * record.
 *
 * PER is estimated from gaps in received `rx` sequence numbers — on the far end (the
 * phone) each received packet prints `rx seq=`, so missing seqs are lost packets.
 */
import { decodeLine } from './decode';

export type RssiStats = { last: number; min: number; max: number; avg: number };
export type Stats = {
  packets: number;
  seq: number | null;
  rxtot: number | null;
  rssi: RssiStats | null;
  snr: { last: number } | null;
  perPct: number | null;
};
export type Series = { rssi: number[]; snr: number[]; frssi: number[] };

export type Telemetry = {
  push(line: string): void;
  stats(): Stats;
  series(): Series;
  reset(): void;
};

const DEFAULT_CAP = 120;

export function createTelemetry(cap: number = DEFAULT_CAP): Telemetry {
  let packets = 0;
  let lastSeq: number | null = null;
  let lastRxtot: number | null = null;
  let lastSnr: number | null = null;
  let rssiLast: number | null = null;
  let rssiMin = Infinity;
  let rssiMax = -Infinity;
  let rssiSum = 0;
  let rssiCount = 0;
  const rxSeqs = new Set<number>();
  let rssi: number[] = [];
  let snr: number[] = [];
  let frssi: number[] = [];

  const trim = (a: number[]): number[] => (a.length > cap ? a.slice(a.length - cap) : a);

  return {
    push(line) {
      const { verb, fields } = decodeLine(line);
      if (fields['rxtot'] !== undefined) lastRxtot = fields['rxtot'];
      if (fields['seq'] !== undefined) lastSeq = fields['seq'];
      if (verb === 'rx' && fields['seq'] !== undefined) rxSeqs.add(fields['seq']);

      if (fields['rssi'] === undefined) return; // not a measured packet
      packets++;
      const r = fields['rssi'];
      rssiLast = r;
      rssiMin = Math.min(rssiMin, r);
      rssiMax = Math.max(rssiMax, r);
      rssiSum += r;
      rssiCount++;
      rssi = trim([...rssi, r]);
      if (fields['snr'] !== undefined) {
        lastSnr = fields['snr'];
        snr = trim([...snr, fields['snr']]);
      }
      if (fields['frssi'] !== undefined) frssi = trim([...frssi, fields['frssi']]);
    },

    stats() {
      let perPct: number | null = null;
      if (rxSeqs.size >= 2) {
        const seqs = [...rxSeqs];
        const span = Math.max(...seqs) - Math.min(...seqs) + 1;
        if (span > 0) perPct = Math.round(((span - rxSeqs.size) / span) * 100);
      }
      return {
        packets,
        seq: lastSeq,
        rxtot: lastRxtot,
        rssi:
          rssiCount > 0
            ? { last: rssiLast!, min: rssiMin, max: rssiMax, avg: rssiSum / rssiCount }
            : null,
        snr: lastSnr != null ? { last: lastSnr } : null,
        perPct,
      };
    },

    series() {
      return { rssi: [...rssi], snr: [...snr], frssi: [...frssi] };
    },

    reset() {
      packets = 0;
      lastSeq = lastRxtot = lastSnr = rssiLast = null;
      rssiMin = Infinity;
      rssiMax = -Infinity;
      rssiSum = rssiCount = 0;
      rxSeqs.clear();
      rssi = [];
      snr = [];
      frssi = [];
    },
  };
}
